import { useQuery } from '@tanstack/react-query'
import type { Sections } from '../../shared/sections'
import { api } from './api'

// A circle's home: what it has kept, what's been added lately, and who added it. Every number is a real count from
// the server, of what you can see.
export type Who = { id: string; name: string; portrait: string | null; you: boolean }

// One person's day in "Lately": how much of each thing they added (or that they joined), counted on the server.
export type LatelyRow = {
  by: Who
  day: string
  at: number
  photos: number
  recipes: number
  people: number
  pets: number
  moments: number
  joined: boolean
  started: boolean
}

// What a row holds, fetched when it's opened: the newest of each, with the counts on the row saying how many in all.
export type LatelyDay = {
  albums: {
    id: string
    title: string
    // The photos added to no album, together as one group.
    loose?: boolean
    count: number
    photos: { id: string; ref: string; width: number; height: number }[]
  }[]
  recipes: { id: string; title: string; cover: string | null; book: { id: string; title: string } | null }[]
  people: { id: string; name: string; pet: boolean; living: boolean; portrait: string | null }[]
  moments: { id: string; title: string }[]
}

export type Lately = { recipes: number; photos: number; people: number; joined: number }
export type HomeData = {
  days: number
  totals: {
    recipes: number
    books: number
    photos: number
    albums: number
    people: number
    pets: number
    members: number
    moments: number
  }
  lately: Lately
  previews: {
    recipes: string[]
    photos: { id: string; ref: string; width: number; height: number }[]
    // The first photo of each of the newest moments that have one.
    moments: string[]
    people: { id: string; name: string; portrait: string | null }[]
  }
  rows: LatelyRow[]
}

// Days are the viewer's own calendar days, so the server needs their offset from UTC.
const query = (params: Record<string, string | undefined>) => {
  const q = new URLSearchParams({ tz: String(-new Date().getTimezoneOffset()) })
  for (const [key, value] of Object.entries(params)) if (value) q.set(key, value)
  return q.toString()
}

export const useHome = (familyId: string, spaceId?: string) =>
  useQuery({
    queryKey: ['home', familyId, spaceId ?? 'all'],
    queryFn: () => api<HomeData>(`/families/${familyId}/home?${query({ space: spaceId })}`),
  })

export const latelyDayQuery = (familyId: string, row: Pick<LatelyRow, 'by' | 'day'>, spaceId?: string) => ({
  queryKey: ['home', familyId, spaceId ?? 'all', row.by.id, row.day],
  queryFn: () =>
    api<LatelyDay>(`/families/${familyId}/home/day?${query({ space: spaceId, by: row.by.id, day: row.day })}`),
})

export const useLatelyDay = (familyId: string, row: LatelyRow, spaceId: string | undefined, open: boolean) =>
  useQuery({ ...latelyDayQuery(familyId, row, spaceId), enabled: open })

// Home with the sections the owner has switched off left out: no line, count or row from them. Who joined stays, except
// in your own space, which no one joins: making it isn't news.
export function onlySections(data: HomeData, on: Sections, personal = false): HomeData {
  return {
    ...data,
    lately: {
      ...data.lately,
      joined: personal ? 0 : data.lately.joined,
      recipes: on.recipes ? data.lately.recipes : 0,
      photos: on.photos ? data.lately.photos : 0,
      people: on.people ? data.lately.people : 0,
    },
    rows: data.rows
      .map((row) => ({
        ...row,
        photos: on.photos ? row.photos : 0,
        recipes: on.recipes ? row.recipes : 0,
        people: on.people ? row.people : 0,
        pets: on.people ? row.pets : 0,
        moments: on.moments ? row.moments : 0,
        joined: !personal && row.joined,
      }))
      .filter((row) => row.joined || row.photos + row.recipes + row.people + row.pets + row.moments > 0),
  }
}

export const counted = (n: number, one: string, many = `${one}s`) =>
  `${n.toLocaleString('en-US')} ${n === 1 ? one : many}`

// Faces for a card with room for `slots` circles: everyone if they fit, otherwise one circle fewer and a count of
// everyone else on the tree (with or without a portrait), so the last circle reads "+N".
export function fitFaces<T>(faces: T[], onTree: number, slots: number) {
  if (onTree <= slots && faces.length <= slots) return { shown: faces, more: 0 }
  const shown = faces.slice(0, slots - 1)
  return { shown, more: Math.max(onTree, faces.length) - shown.length }
}

// "a, b and c"
export function listed(parts: string[]) {
  if (parts.length < 2) return parts.join('')
  return `${parts.slice(0, -1).join(', ')} and ${parts.at(-1)}`
}

// The line under the welcome: what the circle added over the last few weeks, naming only what actually happened. In
// your own space that's what you added, and in Everyone what everyone you know shared with you.
export function latelyLine(lately: Lately, days: number, tree = 'the family tree', personal = false, everyone = false) {
  const who = personal ? 'you' : everyone ? 'everyone' : 'your circle'
  const added = [
    lately.photos && counted(lately.photos, 'photo'),
    lately.recipes && counted(lately.recipes, 'recipe'),
    lately.people && `${counted(lately.people, 'name')} to ${tree}`,
  ].filter((x): x is string => !!x)
  const joined = lately.joined ? `${counted(lately.joined, 'person', 'people')} joined` : ''
  const when = `the last ${days} days`
  if (added.length && joined) return `Over ${when}, ${who} added ${listed(added)}, and ${joined}.`
  if (added.length) return `Over ${when}, ${who} added ${listed(added)}.`
  if (joined) return `Over ${when}, ${joined}.`
  return personal
    ? `It’s been quiet over ${when}. Everything you’ve kept is here, just for you.`
    : everyone
      ? `It’s been quiet over ${when}. Everything shared with you is here.`
      : `It’s been quiet over ${when}. Everything your circle has kept is here.`
}

// "a", "a and b", "a, b, and c"
export function serial(parts: string[]) {
  if (parts.length < 3) return parts.join(' and ')
  return `${parts.slice(0, -1).join(', ')}, and ${parts.at(-1)}`
}

// What someone did on one day, in one sentence without the repeats: "added 22 photos, 3 recipes, and 4 family
// members", "joined the circle and added 1 photo", "wrote 2 moments". A casual circle's tree isn't family.
export function didWhat(row: LatelyRow, family = true) {
  const added = [
    row.photos && counted(row.photos, 'photo'),
    row.recipes && counted(row.recipes, 'recipe'),
    row.people && (family ? counted(row.people, 'family member') : `${counted(row.people, 'name')} to the tree`),
    row.pets && counted(row.pets, 'pet'),
  ].filter((x): x is string => !!x)
  return serial(
    [
      row.joined && (row.started ? 'started this circle' : 'joined the circle'),
      added.length > 0 && `added ${serial(added)}`,
      row.moments > 0 && `wrote ${counted(row.moments, 'moment')}`,
    ].filter((x): x is string => !!x),
  )
}

const DAY = 24 * 60 * 60 * 1000
const calendar = (y: number, m: number, d: number) => Date.UTC(y, m, d) / DAY

// A row's day, the way you'd say it: "today", "yesterday", a weekday this past week, then the date ("Sep 28").
export function dayLabel(day: string, now = new Date()) {
  const [y, m, d] = day.split('-').map(Number) as [number, number, number]
  const days = calendar(now.getFullYear(), now.getMonth(), now.getDate()) - calendar(y, m - 1, d)
  if (days <= 0) return 'today'
  if (days === 1) return 'yesterday'
  const date = new Date(y, m - 1, d)
  if (days < 7) return date.toLocaleDateString('en-US', { weekday: 'long' })
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

// "On this day": what was photographed or happened on today's date in earlier years, by year, newest first. Only full
// dates count. Each year's counts are real; `shown` is the first few.
export type OnThisDayYear = {
  year: number
  photos: { count: number; shown: { id: string; ref: string; width: number; height: number }[] }
  moments: {
    count: number
    shown: { id: string; title: string; photo: string | null; by: { name: string; you: boolean } }[]
  }
}
export type OnThisDay = { day: string; years: OnThisDayYear[] }

// The viewer's own today, as YYYY-MM-DD.
export function localDay(now = new Date()) {
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`
}

export const useOnThisDay = (familyId: string, spaceId: string | undefined, day: string, enabled: boolean) =>
  useQuery({
    queryKey: ['home', familyId, spaceId ?? 'all', 'on-this-day', day],
    queryFn: () => api<OnThisDay>(`/families/${familyId}/home/on-this-day?${query({ space: spaceId, day })}`),
    enabled,
  })

// Only the kinds whose sections are on; a year left with nothing is left out.
export function onThisDayShown(data: OnThisDay, on: Pick<Sections, 'photos' | 'moments'>): OnThisDayYear[] {
  const none = { count: 0, shown: [] }
  return data.years
    .map((year) => ({
      ...year,
      photos: on.photos ? year.photos : none,
      moments: on.moments ? year.moments : none,
    }))
    .filter((year) => year.photos.count + year.moments.count > 0)
}

// "1 year ago", "12 years ago", counted from the viewer's today.
export function yearsAgo(year: number, day: string) {
  const n = Number(day.slice(0, 4)) - year
  return n === 1 ? '1 year ago' : `${n.toLocaleString('en-US')} years ago`
}

// "October 15", for the day the section is about.
export function monthDay(day: string) {
  const [y, m, d] = day.split('-').map(Number) as [number, number, number]
  return new Date(y, m - 1, d).toLocaleDateString('en-US', { month: 'long', day: 'numeric' })
}

// The same day as a story (its Storybook version): every year, oldest first, with each photo's back (caption, date,
// who's in it, who added it) and each moment's words, photos and people. Counts are real; `shown` may stop short.
export type StoryPerson = { id: string; name: string; living: boolean; portrait: string | null }
export type OnThisDayStoryYear = {
  year: number
  photos: {
    count: number
    shown: {
      id: string
      ref: string
      width: number
      height: number
      caption: string
      takenOn: string
      by: string
      people: StoryPerson[]
    }[]
  }
  moments: {
    count: number
    shown: {
      id: string
      title: string
      body: string
      by: { name: string; you: boolean }
      photos: { id: string; width: number; height: number }[]
      people: StoryPerson[]
    }[]
  }
}
export type OnThisDayStory = { day: string; years: OnThisDayStoryYear[] }

export const useOnThisDayStory = (familyId: string, spaceId: string | undefined, day: string) =>
  useQuery({
    queryKey: ['home', familyId, spaceId ?? 'all', 'on-this-day', day, 'story'],
    queryFn: () =>
      api<OnThisDayStory>(`/families/${familyId}/home/on-this-day/story?${query({ space: spaceId, day })}`),
  })
